// src/common/ui/data-normalizers.js
// Data normalization utilities for audit results

import { formatCitationType } from '../utils/type-formatter.js';

const RESULT_NESTED_KEYS = [
  'citation',
  'resolve',
  'resolution',
  'resolve_result',
  'analysis',
  'analyze',
  'analyze_result',
  'congruence_result',
];

const RESULT_ID_KEYS = [
  'id',
  'cite_id',
  'citation_id',
  'citationId',
  'reference_id',
  'identifier',
];

/**
 * Unwrap nested result payload to find the actual data
 * @param {*} payload - Result payload
 * @param {number} depth - Current recursion depth
 * @returns {*} - Unwrapped payload
 */
export function unwrapResultPayload(payload, depth = 0) {
  if (!payload || typeof payload !== 'object' || depth > 5) return payload;

  // Fast path: if the payload already looks like a map of citations, return it
  if (Array.isArray(payload) || payload instanceof Map) return payload;

  const citationLikeKeys = ['citations', 'results', 'entries'];
  for (const key of citationLikeKeys) {
    const val = payload[key];
    if (val && (Array.isArray(val) || typeof val === 'object')) {
      return unwrapResultPayload(val, depth + 1);
    }
  }

  const nestedKeys = ['result', 'data', 'payload'];
  for (const key of nestedKeys) {
    const val = payload[key];
    if (val && typeof val === 'object') {
      return unwrapResultPayload(val, depth + 1);
    }
  }

  return payload;
}

/**
 * Flatten nested citation entry data
 * @param {Object} entry - Citation entry
 * @returns {Object} - Flattened entry
 */
export function flattenCitationEntry(entry) {
  if (!entry || typeof entry !== 'object') return entry;

  const combined = { ...entry };

  // Prefer congruence_result fields over any existing top-level fields when present.
  // This ensures that a fresh congruence run (e.g. after "Check Again") always
  // drives the visible label/calibration/rationale, even if older cached data
  // left stale values on the top-level entry.
  if (entry.congruence_result && typeof entry.congruence_result === 'object') {
    const cr = entry.congruence_result;
    if (cr.label != null) {
      combined.label = cr.label;
    }
    if (cr.calibration != null) {
      combined.calibration = cr.calibration;
    }
    if (cr.rationale != null) {
      combined.rationale = cr.rationale;
    }
    // Copy secondary_labels for quote issues and other secondary verdicts
    // The LLM may return "supports" as main label but flag "quote-mismatch" as secondary
    if (cr.secondary_labels != null && Array.isArray(cr.secondary_labels)) {
      combined.secondary_labels = cr.secondary_labels;
    }
    // NOTE: Do NOT copy cr.type - it's the congruence check type ("legal"/"factual"/"other"),
    // not the citation source type. The source type is handled separately below.
  }

  for (const key of RESULT_NESTED_KEYS) {
    const val = entry[key];
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      for (const [innerKey, innerVal] of Object.entries(val)) {
        if (combined[innerKey] === undefined) {
          combined[innerKey] = innerVal;
        }
      }
    }
  }

  // Common fallbacks so downstream rendering can rely on the flattened keys
  if (combined.cite == null) {
    if (entry.source?.raw != null) combined.cite = entry.source.raw;
    else if (entry.citation?.source?.raw != null) combined.cite = entry.citation.source.raw;
  }
  if (combined.type == null) {
    if (entry.source?.type != null) combined.type = formatCitationType(entry.source.type);
    else if (entry.citation?.source?.type != null) combined.type = formatCitationType(entry.citation.source.type);
  }
  if (combined.quote == null && entry.citation?.quote != null) {
    combined.quote = entry.citation.quote;
  }
  if (combined.snips == null) {
    if (entry.analysis?.snips != null) combined.snips = entry.analysis.snips;
    else if (entry.resolve?.snips != null) combined.snips = entry.resolve.snips;
  }
  if (combined.quote_check == null) {
    if (entry.analysis?.quote_check != null) combined.quote_check = entry.analysis.quote_check;
    else if (entry.quoteCheck != null) combined.quote_check = entry.quoteCheck;
    else if (entry.quote_result != null) combined.quote_check = entry.quote_result;
  }
  if (combined.calibration == null) {
    if (entry.analysis?.calibration != null) combined.calibration = entry.analysis.calibration;
    else if (entry.congruence_result?.calibration != null) combined.calibration = entry.congruence_result.calibration;
  }
  if (combined.rationale == null) {
    if (entry.analysis?.rationale != null) combined.rationale = entry.analysis.rationale;
    else if (entry.congruence_result?.rationale != null) combined.rationale = entry.congruence_result.rationale;
  }
  if (combined.label == null && entry.congruence_result?.label != null) {
    combined.label = entry.congruence_result.label;
  }
  // NOTE: Do NOT copy congruence_result.type - see comment above about congruence check type vs citation source type
  if (combined.doc == null && entry.resolve_result?.doc != null) {
    combined.doc = entry.resolve_result.doc;
  }
  if (combined.file == null && entry.resolve_result?.file_id != null) {
    combined.file = entry.resolve_result.file_id;
  }
  if (combined.method == null && entry.resolve_result?.method != null) {
    combined.method = entry.resolve_result.method;
  }
  if (combined.failure_reason == null && entry.resolve_result?.failure_reason != null) {
    combined.failure_reason = entry.resolve_result.failure_reason;
  }
  if (combined.url == null) {
    const urlCandidates = [
      entry.resolve_result?.url,
      entry.resolve_result?.source_url,
      entry.resolve_result?.best_url,
      entry.resolve_result?.direct_url,
      entry.source_url,
      entry.url_best,
      entry.best_url,
    ];
    for (const candidate of urlCandidates) {
      if (candidate) {
        combined.url = candidate;
        break;
      }
    }
  }
  if (combined.paywalled == null && entry.resolve_result?.paywalled != null) {
    combined.paywalled = entry.resolve_result.paywalled;
  }
  if (combined.web_exists == null && entry.resolve_result?.web_exists != null) {
    combined.web_exists = entry.resolve_result.web_exists;
  }
  if (combined.blocked == null && entry.resolve_result?.blocked != null) {
    combined.blocked = entry.resolve_result.blocked;
  }
  if (combined.label == null) {
    if (entry.analysis?.label != null) combined.label = entry.analysis.label;
    else if (entry.resolve_result?.label != null) combined.label = entry.resolve_result.label;
  }
  if (combined.status == null) {
    if (entry.analysis?.status != null) combined.status = entry.analysis.status;
    else if (entry.resolve_result?.status != null) combined.status = entry.resolve_result.status;
  }
  if (combined.id == null && entry.citation?.id != null) {
    combined.id = entry.citation.id;
  }
  if (combined.file == null && entry.file_id != null) {
    combined.file = entry.file_id;
  }

  return combined;
}

/**
 * Normalize entry ID from various possible fields
 * @param {Object} entry - Entry object
 * @param {string|number} fallback - Fallback ID
 * @returns {string} - Normalized ID
 */
export function normalizeEntryId(entry, fallback) {
  const candidates = [];

  if (entry && typeof entry === 'object') {
    for (const key of RESULT_ID_KEYS) {
      const val = entry[key];
      if (val != null && String(val).trim() !== '') candidates.push(val);
    }
    if (entry.citation && typeof entry.citation === 'object') {
      for (const key of RESULT_ID_KEYS) {
        const val = entry.citation[key];
        if (val != null && String(val).trim() !== '') candidates.push(val);
      }
    }
  }

  if (typeof fallback === 'string' && fallback) {
    candidates.push(fallback);
  } else if (typeof fallback === 'number') {
    candidates.push(fallback + 1);
  }

  const first = candidates.find(v => v != null && String(v).trim() !== '');
  if (first != null) return String(first);

  if (typeof fallback === 'string' && fallback) return fallback;
  if (typeof fallback === 'number') return `entry-${fallback + 1}`;
  return 'entry';
}

/**
 * Check if entry should be rendered
 * @param {string} key - Entry key
 * @param {*} value - Entry value
 * @returns {boolean}
 */
export function shouldRenderAuditEntry(key, value) {
  if (typeof key === 'string' && key.startsWith('__')) return true;
  if (!value || typeof value !== 'object') return false;
  
  const interestingKeys = [
    'label',
    'status',
    'quote_check',
    'quoteCheck',
    'snips',
    'rationale',
    'message',
    'method',
    'file',
    'doc',
    'cite',
    'url',
    'calibration',
    'quote',
    'paywalled',
    'web_exists',
    'blocked',
    'failure_reason',
    'source',
    'resolve_result',
    'analysis',
    'citation',
    'atomic_claims',
    'atomic_claim_verdicts',
    'citation_function',
  ];
  
  return interestingKeys.some(k => value[k] !== undefined);
}

/**
 * Normalize audit entries into a consistent format
 * @param {*} payload - Raw audit payload
 * @returns {Array<[string, Object]>} - Array of [id, data] tuples
 */
export function normalizeAuditEntries(payload) {
  if (payload == null) return [];

  const unwrapped = unwrapResultPayload(payload);
  const entries = [];

  const pushEntry = (rawKey, rawValue, indexHint = null) => {
    if (rawValue && typeof rawValue === 'object' && !(rawValue instanceof Date)) {
      const flat = flattenCitationEntry(rawValue);
      const cid = normalizeEntryId(flat, rawKey ?? indexHint);
      entries.push([cid, flat]);
    } else {
      const cid = normalizeEntryId({}, rawKey ?? indexHint);
      entries.push([cid, rawValue]);
    }
  };

  if (unwrapped instanceof Map) {
    let idx = 0;
    for (const [key, value] of unwrapped.entries()) {
      pushEntry(key, value, idx++);
    }
  } else if (Array.isArray(unwrapped)) {
    unwrapped.forEach((value, idx) => pushEntry(null, value, idx));
  } else if (typeof unwrapped === 'object') {
    Object.entries(unwrapped).forEach(([key, value]) => pushEntry(key, value));
  }

  if (!entries.length && payload !== unwrapped && typeof payload === 'object') {
    Object.entries(payload).forEach(([key, value]) => pushEntry(key, value));
  }

  const filtered = entries.filter(([key, value]) => shouldRenderAuditEntry(key, value));
  return filtered.length ? filtered : entries;
}
